import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { CAPS } from '@/lib/kbCaps';
import { extractKbFieldErrors, type FieldErrorMap } from '@/lib/kbFieldErrors';
import { FieldError } from '@/components/onboarding/enrichment/FieldError';
import { CharCounter } from '@/components/shared/CharCounter';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { cn } from '@/lib/utils';
import type { LinkLibraryItem } from '@/services/kbProfileService';

const MAX_LINKS = CAPS.link_library_items;

function makeEmptyLink(): LinkLibraryItem {
  return { label: '', url: '' };
}

interface LinksEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialLinks: LinkLibraryItem[];
}

export function LinksEditor({ open, onOpenChange, initialLinks }: LinksEditorProps) {
  const { t } = useTranslation();
  const { updateKbProfile } = useOnboardingState();
  const [links, setLinks] = useState<LinkLibraryItem[]>(
    initialLinks.length > 0 ? [...initialLinks] : [makeEmptyLink()]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);

  function updateLink(idx: number, patch: Partial<LinkLibraryItem>) {
    setLinks((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  function addLink() {
    if (links.length < MAX_LINKS) {
      setLinks((prev) => [...prev, makeEmptyLink()]);
    }
  }

  function removeLink(idx: number) {
    setLinks((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setFieldErrors(null);
    try {
      const valid = links.filter((l) => l.label.trim() && l.url.trim());
      await updateKbProfile({ link_library: valid });
      onOpenChange(false);
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  // The payload drops incomplete links, so the backend's list index (the 422
  // path `link_library.1.url`) can differ from the UI index.
  function payloadIndex(uiIndex: number): number {
    return links.slice(0, uiIndex + 1).filter((l) => l.label.trim() && l.url.trim()).length - 1;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('bot.enrich.links.title')}</DialogTitle>
          <DialogDescription>{t('bot.enrich.links.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[min(60dvh,calc(100dvh-16rem))] overflow-y-auto pr-1">
          {links.map((link, idx) => (
            <div key={idx} className="space-y-3 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('bot.enrich.links.link_label', { n: idx + 1 })}
                </span>
                {links.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('bot.enrich.links.remove')}
                    className={cn('h-7 w-7', HIT_AREA_44)}
                    onClick={() => removeLink(idx)}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`link-label-${idx}`}>{t('bot.enrich.links.label_field')}</Label>
                <Input
                  id={`link-label-${idx}`}
                  value={link.label}
                  onChange={(e) => updateLink(idx, { label: e.target.value })}
                  placeholder={t('bot.enrich.links.label_placeholder')}
                  maxLength={CAPS.link_label}
                  className="min-h-11"
                />
                <FieldError errors={fieldErrors} path={`link_library.${payloadIndex(idx)}.label`} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`link-url-${idx}`}>{t('bot.enrich.links.url_field')}</Label>
                <Input
                  id={`link-url-${idx}`}
                  type="url"
                  value={link.url}
                  onChange={(e) => updateLink(idx, { url: e.target.value })}
                  placeholder="https://"
                  maxLength={CAPS.link_url}
                  className="min-h-11"
                />
                <FieldError errors={fieldErrors} path={`link_library.${payloadIndex(idx)}.url`} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`link-when-${idx}`}>{t('bot.enrich.links.when_field')}</Label>
                <Input
                  id={`link-when-${idx}`}
                  value={link.when_to_send ?? ''}
                  onChange={(e) => updateLink(idx, { when_to_send: e.target.value || undefined })}
                  placeholder={t('bot.enrich.links.when_placeholder')}
                  maxLength={CAPS.link_when_to_send}
                  className="min-h-11"
                />
                <CharCounter
                  value={link.when_to_send ?? ''}
                  max={CAPS.link_when_to_send}
                  mode="near-limit"
                />
              </div>
            </div>
          ))}
          {links.length < MAX_LINKS && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addLink}
              className="min-h-11 w-full"
            >
              <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
              {t('bot.enrich.links.add')}
            </Button>
          )}
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="min-h-11"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-11">
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
